import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from "@/components/shared/Grid"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useArtistsData } from '@/contexts/ArtistsDataContext'

export default function GridTestBench() {
  const { t } = useTranslation()
  const { availableArtists, loading: loadingArtists } = useArtistsData()
  const [key, setKey] = useState(0)
  const [isCleared, setIsCleared] = useState(false)
  const [currentArtistIndex, setCurrentArtistIndex] = useState(0)

  // Simulate completely fresh environment on mount
  useEffect(() => {
    // Clear all storage mechanisms
    sessionStorage.clear()
    localStorage.clear()
    
    // Clear IndexedDB
    if (window.indexedDB) {
      indexedDB.databases?.().then((dbs) => {
        dbs.forEach(db => {
          if (db.name) {
            indexedDB.deleteDatabase(db.name)
          }
        })
      })
    }
    
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach(name => {
          caches.delete(name)
        })
      })
    }
    
    // Force component remount with fresh key
    setKey(prev => prev + 1)
    setIsCleared(true)
  }, [])

  // Handle page reloads by forcing a complete reset
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.clear()
      localStorage.clear()
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const currentArtist = availableArtists[currentArtistIndex]

  const handlePrevArtist = () => {
    setCurrentArtistIndex(prev => (prev - 1 + availableArtists.length) % availableArtists.length)
  }

  const handleNextArtist = () => {
    setCurrentArtistIndex(prev => (prev + 1) % availableArtists.length)
  }

  return (
    <div className="min-h-screen bg-background transition-colors" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
      <div className="sticky top-0 md:top-[52px] z-40 bg-background/95 backdrop-blur-md border-b border-border/50 dark:border-border/30 rounded-b-2xl">
        <div className="phone-container-padded py-3">
          <h1 className="text-2xl font-bold text-foreground">Grid Test Bench</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Testing grid loading behavior with fresh data on every page reload
          </p>
          {isCleared && (
            <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-green-700 dark:text-green-400 text-xs">
              ✓ All caches cleared (localStorage, sessionStorage, IndexedDB, Service Worker caches)
            </div>
          )}
        </div>
      </div>
      
      <div className="phone-container-padded">
        {/* Artist Selector */}
        <div className="flex items-center justify-between gap-3 mb-6 mt-4">
          <Button
            onClick={handlePrevArtist}
            disabled={availableArtists.length === 0}
            variant="outline"
            size="icon"
            className="flex-shrink-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="flex-1 text-center">
            {loadingArtists ? (
              <p className="text-sm text-muted-foreground">Loading artists...</p>
            ) : currentArtist ? (
              <div className="flex flex-col gap-1">
                <p className="text-lg font-semibold text-foreground">{currentArtist.displayName || currentArtist.firstName}</p>
                <p className="text-xs text-muted-foreground">
                  {currentArtistIndex + 1} of {availableArtists.length}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No artists found</p>
            )}
          </div>
          
          <Button
            onClick={handleNextArtist}
            disabled={availableArtists.length === 0}
            variant="outline"
            size="icon"
            className="flex-shrink-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Grid Display */}
        <div className="animate-in fade-in duration-500">
          {currentArtist ? (
            <Grid
              key={`${key}-${currentArtist.id}`}
              selectedCollections={['flashes', 'portfolioItems']}
              selectedStyle={t('onskin.filters.allStyles')}
              selectedArtist={currentArtist.id}
              selectedLocations={[t('onskin.filters.allLocations')]}
              sorting="recent"
              includeBooked={false}
              showDeals={false}
            />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              {loadingArtists ? 'Loading...' : 'No artists available'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
